<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: GeneticMatches                                        |
| Section: views/overviews                                         |
| Purpose: DNA match analysis page with cards, segments, and graph.|
+------------------------------------------------------------------+
-->
<template>
    <div class="wrapper overview-wrapper overview-gap-72">
        <div class="overview-header">
            <h1>{{ $t('geneticMatches.title') }}</h1>
            <p class="subtitle">{{ $t('geneticMatches.subtitle') }}</p>

            <div class="view-mode-controls">
                <span class="view-mode-label">{{ $t('geneticMatches.viewModeLabel') }}</span>
                <button v-for="mode in viewModes" :key="mode" :aria-pressed="selectedViewMode === mode"
                    :class="{ active: selectedViewMode === mode }" @click="setViewMode(mode)">
                    {{ $t(`geneticMatches.viewModes.${mode}`) }}
                </button>
            </div>

            <!-- Filter Buttons -->
            <div v-if="showCategoryButtons" class="category_buttons">
                <button v-for="category in categoryOptions" :key="category"
                    :aria-pressed="selectedCategory === category"
                    :class="{ active: selectedCategory === category, disabled: isCategoryDisabled(category) }"
                    :disabled="isCategoryDisabled(category)"
                    @click="setCategory(category)">
                    {{ getCategoryLabel(category) }}<template v-if="showCategoryCounts"> ({{ getCategoryCount(category) }})</template>
                </button>
                <!-- Temporarily hidden until data is available -->
                <!-- <button :class="{ active: selectedCategory === 'Y-DNA' }" @click="setCategory('Y-DNA')">
                    Y-DNA
                </button>
                <button :class="{ active: selectedCategory === 'mtDNA' }" @click="setCategory('mtDNA')">
                    mtDNA
                </button> -->
            </div>

            <!-- Sort Controls -->
            <div v-if="showSortControls" class="sort-controls">
                <span class="sort-label">{{ $t('geneticMatches.sortLabel') }}</span>
                <button :aria-pressed="selectedSort === 'dna'" :class="{ active: selectedSort === 'dna' }"
                    @click="setSort('dna')">
                    {{ $t('geneticMatches.sortOptions.dna') }}
                    <img v-if="selectedSort === 'dna'" :src="sortIconSrc" class="sort-direction-icon" alt="" />
                </button>
                <button :aria-pressed="selectedSort === 'segments'" :class="{ active: selectedSort === 'segments' }"
                    @click="setSort('segments')">
                    {{ $t('geneticMatches.sortOptions.segments') }}
                    <img v-if="selectedSort === 'segments'" :src="sortIconSrc" class="sort-direction-icon" alt="" />
                </button>
                <button :aria-pressed="selectedSort === 'largestSegment'"
                    :class="{ active: selectedSort === 'largestSegment' }" @click="setSort('largestSegment')">
                    {{ $t('geneticMatches.sortOptions.largestSegment') }}
                    <img v-if="selectedSort === 'largestSegment'" :src="sortIconSrc" class="sort-direction-icon"
                        alt="" />
                </button>
                <button :aria-pressed="selectedSort === 'name'" :class="{ active: selectedSort === 'name' }"
                    @click="setSort('name')">
                    {{ $t('geneticMatches.sortOptions.name') }}
                    <img v-if="selectedSort === 'name'" :src="sortIconSrc" class="sort-direction-icon" alt="" />
                </button>
            </div>
        </div>

        <div v-if="selectedViewMode === 'network'" class="dna-network-container">
            <DNARelationNetwork class="network-mode-view" :matches="dnaMatches" :testers="dnaTesters"
                :tester-order="dnaTesterOrder" />
        </div>

        <div v-if="selectedViewMode === 'cards'" class="dna-matches">
            <PersonCardDNA v-for="match in filteredMatches" :key="match.matchKey || match.personID" :photoSrc="match.photoSrc"
                :name="match.name" :years="match.years" :gender="match.gender"
                :href="getMatchHref(match)" :overlay-label="getMatchOverlayLabel(match)"
                :private-overlay-label="getMatchPrivateOverlayLabel(match)" :living="match.living"
                :personID="match.personID" :placeholder="match.placeholder" :isSmall="true" :showDate="true" :dnaInfo="match.dnaInfo" />
            <p v-if="filteredMatches.length === 0" class="empty-state">{{ $t('geneticMatches.noMatches') }}</p>
        </div>

        <div v-else-if="selectedViewMode === 'chromosomeBrowser'" class="browser-mode-layout">
            <aside class="browser-sidebar">
                <div class="browser-submode-controls">
                    <button :aria-pressed="browserSubmode === 'ancestorPainting'"
                        :class="{ active: browserSubmode === 'ancestorPainting' }"
                        @click="setBrowserSubmode('ancestorPainting')">
                        {{ $t('geneticMatches.chromosomeBrowser.submodes.ancestorPainting') }}
                    </button>
                    <button :aria-pressed="browserSubmode === 'match'" :class="{ active: browserSubmode === 'match' }"
                        @click="setBrowserSubmode('match')">
                        {{ $t('geneticMatches.chromosomeBrowser.submodes.match') }}
                    </button>
                </div>

                <template v-if="browserSubmode === 'match'">
                    <h3>{{ $t('geneticMatches.chromosomeBrowser.matchListTitle') }}</h3>
                    <p v-if="availableBrowserMatches.length === 0" class="empty-state browser-empty-state">
                        {{ $t('geneticMatches.chromosomeBrowser.noAvailableCsv') }}
                    </p>
                    <button v-for="match in availableBrowserMatches" :key="match.entryId" class="browser-match-button"
                        :class="{ active: selectedBrowserEntryId === match.entryId }"
                        @click="selectBrowserMatch(match.entryId)">
                        <strong>{{ getBrowserMatchDisplayName(match) }}</strong>
                        <span>{{ getBrowserMatchMeta(match) }}</span>
                    </button>
                </template>

                <template v-else-if="browserSubmode === 'ancestorPainting'">
                    <div class="painting-summary-list" v-if="filteredPaintingSummaryByAncestor.length > 0">
                        <h4>{{ $t('geneticMatches.chromosomeBrowser.painting.summaryTitle') }}</h4>
                        <button v-for="summary in filteredPaintingSummaryByAncestor" :key="summary.ancestorKey"
                            class="painting-summary-row" type="button"
                            :class="{ active: selectedPaintingAncestorKey === summary.ancestorKey }"
                            :aria-pressed="selectedPaintingAncestorKey === summary.ancestorKey"
                            @click="togglePaintingAncestorKey(summary.ancestorKey)">
                            <span class="painting-summary-main">
                                <i class="painting-summary-color"
                                    :style="{ background: colorForAncestor(summary.ancestorKey) }" />
                                <strong :title="summary.ancestorLabel">{{ formatAncestorSummaryLabel(summary.ancestorLabel) }}</strong>
                            </span>
                        </button>
                    </div>
                </template>

            </aside>

            <ChromosomeBrowser v-if="browserSubmode === 'match'" class="browser-view" :selected-label="selectedBrowserLabel"
                :tester-person="selectedBrowserTesterInfo" :match-person="selectedBrowserMatchInfo"
                :segments="browserSegments" :summary="browserSummary" :loading="browserLoading" :error="browserError"
                :show-ancestry-overlay="showAncestryOverlay" :ancestry-loading="browserAncestryLoading"
                :ancestry-overlay="browserAncestryOverlay" @update:show-ancestry-overlay="setShowAncestryOverlay" />
            <AncestorChromosomePainting v-else class="browser-view" :assignments="filteredPaintingAssignments"
                :testers="ancestorPainting.testers" :selected-ancestor-key="selectedPaintingAncestorKey"
                :autosomal-cm-total-estimate="ancestorPainting.totals.autosomalCmTotalEstimate"
                :loading="ancestorPaintingLoading" :error="ancestorPaintingError" />
        </div>

        <div v-else-if="selectedViewMode === 'reconstructedAncestors'" class="browser-mode-layout reconstructed-mode-layout">
            <aside class="browser-sidebar">
                <div class="painting-summary-list" v-if="filteredReconstructedSummaryByAncestor.length > 0">
                    <button v-for="summary in filteredReconstructedSummaryByAncestor" :key="summary.ancestorKey"
                        class="painting-summary-row reconstructed-summary-row" type="button"
                        :class="{ active: selectedReconstructedAncestorKey === summary.ancestorKey }"
                        :aria-pressed="selectedReconstructedAncestorKey === summary.ancestorKey"
                        @click="toggleReconstructedAncestorKey(summary.ancestorKey)">
                        <span class="painting-summary-main">
                            <strong :title="summary.ancestorLabel">{{ formatAncestorSummaryLabel(summary.ancestorLabel) }}</strong>
                        </span>
                        <span class="reconstructed-summary-meta">
                            {{ formatOneDecimal(summary.assignedCentimorgans) }} cM ·
                            {{ formatInteger(summary.matchCount) }}
                            {{ $t('geneticMatches.chromosomeBrowser.reconstructed.metrics.matches') }}
                        </span>
                    </button>
                </div>
            </aside>

            <ReconstructedAncestorsOverview class="browser-view"
                :ancestors="displayedReconstructedAncestors"
                :totals="displayedReconstructedTotals"
                :loading="reconstructedAncestorsLoading"
                :error="reconstructedAncestorsError" />
        </div>

        <div v-else-if="selectedViewMode === 'haplogroups'" class="haplogroup-mode-view">
            <div class="haplogroup-globe-panel">
                <HaplogroupGlobe :selected-haplogroup-ids="selectedHaplogroupIds"
                    :focused-haplogroup-id="focusedHaplogroupId"
                    @update:selected-haplogroup-ids="setSelectedHaplogroupIds" />
                <HaplogroupProofBoard class="haplogroup-proof-overlay"
                    :selected-haplogroup-ids="selectedHaplogroupIds"
                    @focus-route="focusHaplogroupRoute" />
            </div>
        </div>

    </div>
    <ContactCta />
</template>

<script>
import PersonCardDNA from '@/atoms/PersonCardDNA.vue';
import AncestorChromosomePainting from '@/components/AncestorChromosomePainting.vue';
import ChromosomeBrowser from '@/components/ChromosomeBrowser.vue';
import DNARelationNetwork from '@/components/DNARelationNetwork.vue';
import HaplogroupGlobe from '@/components/HaplogroupGlobe.vue';
import HaplogroupProofBoard from '@/components/HaplogroupProofBoard.vue';
import ReconstructedAncestorsOverview from '@/components/ReconstructedAncestorsOverview.vue';
import ContactCta from '@/components/ContactCta.vue';
import haplogroupData from '@/data/haplogroups.json';
import { fetchDnaMatches, fetchDnaTesters } from '@/api/apiService';
// import { getPersonById } from '@/api/personService';
import { getDefaultPhoto } from '@/utils/photoUtils';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import { useDnaSegments } from '@/composables/useDnaSegments';
import { isPrivateLivingPerson } from '@/utils/personPrivacy';
import { colorForAncestorKey } from '@/utils/ancestorPaintingColors';
import { useRoute, useRouter } from 'vue-router';
// import axios from 'axios';
import { formatYears } from '@/utils/dateUtils';
import { applyGenericSeo, applyBreadcrumbSeo } from '@/utils/seoUtils';
import sortAscSvg from '@/assets/icons/sort_asc.svg';
import sortDscSvg from '@/assets/icons/sort_dsc.svg';

const ALL_CATEGORY = 'Alle';
const UNKNOWN_RELATION = 'Onbekend';
const INVESTIGATION_RELATION = 'In onderzoek: bloedverwant van Josina Akolo';
const DEFAULT_VIEW_MODE = 'cards';
const VIEW_MODES = ['cards', 'chromosomeBrowser', 'reconstructedAncestors', 'network', 'haplogroups'];
const normalizeViewMode = (mode) => VIEW_MODES.includes(mode) ? mode : DEFAULT_VIEW_MODE;
const SORT_FIELDS = ['dna', 'segments', 'largestSegment', 'name'];
const normalizeSortField = (field) => SORT_FIELDS.includes(field) ? field : 'dna';
const normalizeSortAscending = (order, field) => {
    if (order === 'asc') return true;
    if (order === 'desc') return false;
    return field === 'name';
};
const BROWSER_SUBMODES = ['ancestorPainting', 'match'];
const normalizeBrowserSubmode = (mode) => BROWSER_SUBMODES.includes(mode) ? mode : 'ancestorPainting';
const ALL_PAINTING_ANCESTORS = 'all';
const ALL_RECONSTRUCTED_ANCESTORS = 'all';
const DEFAULT_AUTOSOMAL_CM_TOTAL_ESTIMATE = 6800;
const DEFAULT_HAPLOGROUP_ROUTE_IDS = (haplogroupData.haplogroups || [])
    .filter(hg => hg.routeAvailable !== false && Array.isArray(hg.steps) && hg.steps.length > 0)
    .map(hg => hg.id);

// Keep a factory so reset paths always create a fresh summary object and avoid
// accidental shared references between reactive state updates.
const createEmptyBrowserSummary = () => ({
    totalCentimorgans: 0,
    totalSnps: 0,
    chromosomeCount: 0,
    sharedPercentage: 0,
});

const summarizePaintingCoverage = (assignments) => {
    const byAncestor = new Map();

    for (const assignment of assignments) {
        const length = Math.max(Number(assignment.endBp || 0) - Number(assignment.startBp || 0), 1);
        const coverageKey = [
            assignment.testerCategory || 'unknown',
            assignment.parentalSide || 'unphased',
            assignment.chromosome || 'unknown',
        ].join(':');
        const ancestorKey = assignment.ancestorKey || 'unknown';

        if (!byAncestor.has(ancestorKey)) {
            byAncestor.set(ancestorKey, {
                ancestorKey,
                ancestorLabel: assignment.ancestorLabel || ancestorKey,
                centimorgans: 0,
                basePairs: 0,
                assignmentCount: 0,
                coverageByTrack: new Map(),
            });
        }

        const summary = byAncestor.get(ancestorKey);
        summary.assignmentCount += 1;
        if (!summary.coverageByTrack.has(coverageKey)) {
            summary.coverageByTrack.set(coverageKey, []);
        }
        summary.coverageByTrack.get(coverageKey).push({
            startBp: Number(assignment.startBp || 0),
            endBp: Number(assignment.endBp || 0),
            cmPerBp: Number(assignment.centimorgans || 0) / length,
        });
    }

    for (const summary of byAncestor.values()) {
        for (const intervals of summary.coverageByTrack.values()) {
            const sorted = [...intervals]
                .filter(interval => interval.endBp > interval.startBp)
                .sort((a, b) => a.startBp - b.startBp || a.endBp - b.endBp);
            const covered = [];

            for (const interval of sorted) {
                let cursor = interval.startBp;

                for (const existing of covered) {
                    if (existing.endBp <= cursor) {
                        continue;
                    }
                    if (existing.startBp >= interval.endBp) {
                        break;
                    }
                    if (existing.startBp > cursor) {
                        const intervalLength = existing.startBp - cursor;
                        summary.basePairs += intervalLength;
                        summary.centimorgans += intervalLength * interval.cmPerBp;
                    }
                    cursor = Math.max(cursor, existing.endBp);
                    if (cursor >= interval.endBp) {
                        break;
                    }
                }

                if (cursor < interval.endBp) {
                    const intervalLength = interval.endBp - cursor;
                    summary.basePairs += intervalLength;
                    summary.centimorgans += intervalLength * interval.cmPerBp;
                }

                covered.push({ startBp: interval.startBp, endBp: interval.endBp });
                covered.sort((a, b) => a.startBp - b.startBp || a.endBp - b.endBp);
            }
        }
        delete summary.coverageByTrack;
    }

    return [...byAncestor.values()];
};

export default {
    name: 'GeneticMatches',
    components: {
        PersonCardDNA,
        AncestorChromosomePainting,
        ChromosomeBrowser,
        DNARelationNetwork,
        HaplogroupGlobe,
        HaplogroupProofBoard,
        ReconstructedAncestorsOverview,
        ContactCta,
    },
    setup() {
        const { authToken } = useAuth();
        const { localePath, locale } = useLocale();
        const router = useRouter();
        const route = useRoute();
        const {
            buildEntryId,
            loadSegmentEntries,
            loadSegmentsForEntry,
            loadAncestryOverlayForEntry,
            loadAncestorPainting,
            loadReconstructedAncestors,
            summarizeSegments,
        } = useDnaSegments();
        return {
            authToken,
            localePath,
            locale,
            router,
            route,
            buildEntryId,
            loadSegmentEntries,
            loadSegmentsForEntry,
            loadAncestryOverlayForEntry,
            loadAncestorPainting,
            loadReconstructedAncestors,
            summarizeSegments,
        };
    },
    data() {
        const queryCategory = this.route.query.filter;
        const legacyReconstructedMode = this.route.query.view === 'chromosomeBrowser'
            && this.route.query.browserMode === 'reconstructedAncestors';
        const initialViewMode = legacyReconstructedMode
            ? 'reconstructedAncestors'
            : normalizeViewMode(this.route.query.view);
        const initialBrowserSubmode = normalizeBrowserSubmode(this.route.query.browserMode);
        const initialSort = initialViewMode === 'cards'
            ? normalizeSortField(this.route.query.sortField)
            : 'dna';
        const initialSortAscending = initialViewMode === 'cards'
            ? normalizeSortAscending(this.route.query.sortOrder, initialSort)
            : false;
        let initialCategory = queryCategory || ALL_CATEGORY;
        if (initialViewMode === 'reconstructedAncestors') {
            initialCategory = ALL_CATEGORY;
        }

        return {
            dnaMatches: [],
            dnaTesters: {},
            dnaTesterOrder: [],
            selectedCategory: initialCategory,
            selectedSort: initialSort,
            sortAscending: initialSortAscending,
            selectedViewMode: initialViewMode,
            selectedBrowserEntryId: '',
            browserSegments: [],
            browserSummary: createEmptyBrowserSummary(),
            browserLoading: false,
            browserError: '',
            browserEntriesLoading: false,
            browserEntriesError: '',
            browserEntriesIndex: {},
            showAncestryOverlay: false,
            browserAncestryLoading: false,
            browserAncestryOverlay: {
                available: false,
                testerCategory: '',
                slicesBySegmentKey: {},
                legend: [],
            },
            browserSubmode: initialBrowserSubmode,
            ancestorPaintingLoading: false,
            ancestorPaintingError: '',
            ancestorPainting: {
                testers: [],
                assignments: [],
                summaryByAncestor: [],
                summaryByTester: [],
                unresolved: [],
                totals: {
                    assignedCentimorgans: 0,
                    autosomalCmTotalEstimate: 6800,
                    assignmentCount: 0,
                    unresolvedCount: 0,
                },
            },
            reconstructedAncestorsLoading: false,
            reconstructedAncestorsError: '',
            reconstructedAncestors: {
                ancestors: [],
                unresolved: [],
                totals: {
                    reconstructedAncestorCount: 0,
                    assignedCentimorgans: 0,
                    autosomalCmTotalEstimate: 6800,
                    includedSegmentCount: 0,
                    excludedWeakSegmentCount: 0,
                    unresolvedRelationshipCount: 0,
                },
            },
            selectedPaintingAncestorKey: ALL_PAINTING_ANCESTORS,
            selectedReconstructedAncestorKey: ALL_RECONSTRUCTED_ANCESTORS,
            selectedHaplogroupIds: [...DEFAULT_HAPLOGROUP_ROUTE_IDS],
            focusedHaplogroupId: '',
        };
    },
    // async created() {
    //     // Fetch and transform data for each personID
    //     this.dnaMatches = await Promise.all(
    //         dnaMatchesData.map(async (entry) => {
    //             const personData = await getPersonById(entry.personID);
    //             let photoPath = '';

    //             // Fetch photo path if available
    //             try {
    //                 const photoResponse = await axios.get(
    //                     `${process.env.VUE_APP_BACKEND_API_URL}/person-default-photo/${entry.personID}`,
    //                     {
    //                         headers: {
    //                             'X-API-KEY': process.env.VUE_APP_API_KEY
    //                         }
    //                     }
    //                 );
    //                 photoPath = photoResponse.data
    //                     ? `${process.env.VUE_APP_PHOTO_API_URL}/${encodeURIComponent(photoResponse.data.path)}`
    //                     : '';
    //             } catch (error) {
    //                 console.error(`Error fetching profile image for personID ${entry.personID}:`, error);
    //             }

    //             // Determine gender and pass it to getDefaultPhoto
    //             const gender = personData.sex === 'M' ? 'M' : personData.sex === 'F' ? 'F' : null;

    //             return {
    //                 personID: entry.personID,
    //                 category: entry.category,
    //                 photoSrc: photoPath || getDefaultPhoto(gender), // Use fetched photo or fallback
    //                 name: `${personData.firstname} ${personData.lastname}`,
    //                 years: formatYears(
    //                     personData.birthdatetr,
    //                     personData.altbirthdatetr,
    //                     personData.deathdatetr,
    //                     personData.burialdatetr,
    //                     personData.living
    //                 ),
    //                 gender: gender === 'M' ? 'male' : gender === 'F' ? 'female' : 'unknown',
    //                 living: personData.living === 1,
    //                 dnaInfo: entry.dnaInfo
    //             };
    //         })
    //     );
    // },
    mounted() {
        const title = "DNA | Sejarah Thümann";
        const description = "Ontdek DNA-matches en genetische connecties binnen de familie Thümann.";
        const image = "https://sejarah-thumann.online/img/sejarah_thumann1080x781.4fc54a6a.jpg";
        const url = window.location.href;

        applyGenericSeo({ title, description, image, url }, { includeTwitter: true });
        applyBreadcrumbSeo([{ name: 'Home', url: 'https://sejarah-thumann.online/' }, { name: 'DNA', url: 'https://sejarah-thumann.online/dna' }]);

        // Eventuele overige mounted logica, bijvoorbeeld:
        document.title = title;
    },
    methods: {
        isKnownCategory(category) {
            return category === ALL_CATEGORY || this.dnaTesterOrder.includes(category);
        },
        async refreshDnaPrivacyData() {
            try {
                const [testerResponse, matchResponse] = await Promise.all([
                    fetchDnaTesters(),
                    fetchDnaMatches(),
                ]);
                const testers = Array.isArray(testerResponse?.testers) ? testerResponse.testers : [];
                this.dnaTesters = Object.fromEntries(
                    testers.map(tester => [tester.category, tester])
                );
                this.dnaTesterOrder = testers.map(tester => tester.category);
                this.dnaMatches = Array.isArray(matchResponse?.matches) ? matchResponse.matches : [];

                if (!this.isKnownCategory(this.selectedCategory)) {
                    this.selectedCategory = ALL_CATEGORY;
                }
            } catch (error) {
                this.dnaMatches = [];
                this.dnaTesters = {};
                this.dnaTesterOrder = [];
                this.selectedCategory = ALL_CATEGORY;
                console.error('Error loading privacy-filtered DNA matches:', error);
            }
        },
        getBrowserEntry(testerCategory, personID, matchKey = '') {
            const direct = personID
                ? this.browserEntriesIndex[this.buildEntryId(testerCategory, personID)]
                : null;
            if (direct) return direct;
            return Object.values(this.browserEntriesIndex).find(entry => (
                entry.testerCategory === testerCategory && matchKey && entry.matchKey === matchKey
            )) || null;
        },
        hasSegmentCsv(testerCategory, personID, matchKey = '') {
            return Boolean(this.getBrowserEntry(testerCategory, personID, matchKey));
        },
        async refreshBrowserEntries() {
            this.browserEntriesLoading = true;
            this.browserEntriesError = '';

            try {
                // Build an id-index so entry lookups stay O(1) when switching matches.
                const { entries } = await this.loadSegmentEntries();
                const nextIndex = {};

                for (const entry of entries) {
                    if (!entry?.id) {
                        continue;
                    }
                    nextIndex[entry.id] = entry;
                }

                this.browserEntriesIndex = nextIndex;
            } catch (error) {
                this.browserEntriesIndex = {};
                this.browserEntriesError = this.$t('geneticMatches.chromosomeBrowser.loadError');
                console.error(error);
            } finally {
                this.browserEntriesLoading = false;
            }
        },
        async refreshAncestorPainting() {
            if (this.ancestorPainting.totals.assignmentCount > 0 || this.ancestorPaintingLoading) {
                this.ensurePaintingAncestorSelection();
                return;
            }

            this.ancestorPaintingLoading = true;
            this.ancestorPaintingError = '';

            try {
                this.ancestorPainting = await this.loadAncestorPainting();
                this.ensurePaintingAncestorSelection();
            } catch (error) {
                this.ancestorPainting = {
                    testers: [],
                    assignments: [],
                    summaryByAncestor: [],
                    summaryByTester: [],
                    unresolved: [],
                    totals: {
                        assignedCentimorgans: 0,
                        autosomalCmTotalEstimate: 6800,
                        assignmentCount: 0,
                        unresolvedCount: 0,
                    },
                };
                this.ancestorPaintingError = this.$t('geneticMatches.chromosomeBrowser.painting.loadError');
                console.error(error);
                this.ensurePaintingAncestorSelection();
            } finally {
                this.ancestorPaintingLoading = false;
            }
        },
        async refreshReconstructedAncestors() {
            if (this.reconstructedAncestors.ancestors.length > 0 || this.reconstructedAncestorsLoading) {
                this.ensureReconstructedAncestorSelection();
                return;
            }

            this.reconstructedAncestorsLoading = true;
            this.reconstructedAncestorsError = '';

            try {
                this.reconstructedAncestors = await this.loadReconstructedAncestors();
                this.ensureReconstructedAncestorSelection();
            } catch (error) {
                this.reconstructedAncestors = {
                    ancestors: [],
                    unresolved: [],
                    totals: {
                        reconstructedAncestorCount: 0,
                        assignedCentimorgans: 0,
                        autosomalCmTotalEstimate: 6800,
                        includedSegmentCount: 0,
                        excludedWeakSegmentCount: 0,
                        unresolvedRelationshipCount: 0,
                    },
                };
                this.reconstructedAncestorsError = this.$t('geneticMatches.chromosomeBrowser.reconstructed.loadError');
                console.error(error);
                this.ensureReconstructedAncestorSelection();
            } finally {
                this.reconstructedAncestorsLoading = false;
            }
        },
        ensurePaintingAncestorSelection() {
            if (this.selectedPaintingAncestorKey === ALL_PAINTING_ANCESTORS) {
                return;
            }
            const validAncestorKeys = new Set(this.filteredPaintingAssignments.map(assignment => assignment.ancestorKey));
            if (!validAncestorKeys.has(this.selectedPaintingAncestorKey)) {
                this.selectedPaintingAncestorKey = ALL_PAINTING_ANCESTORS;
            }
        },
        togglePaintingAncestorKey(ancestorKey) {
            this.selectedPaintingAncestorKey = this.selectedPaintingAncestorKey === ancestorKey
                ? ALL_PAINTING_ANCESTORS
                : ancestorKey;
        },
        ensureReconstructedAncestorSelection() {
            const firstAncestorKey = this.filteredReconstructedSummaryByAncestor[0]?.ancestorKey;
            if (!firstAncestorKey) {
                this.selectedReconstructedAncestorKey = ALL_RECONSTRUCTED_ANCESTORS;
                return;
            }

            const validAncestorKeys = new Set(this.filteredReconstructedAncestors.map(ancestor => ancestor.ancestorKey));
            if (this.selectedReconstructedAncestorKey === ALL_RECONSTRUCTED_ANCESTORS
                || !validAncestorKeys.has(this.selectedReconstructedAncestorKey)) {
                this.selectedReconstructedAncestorKey = firstAncestorKey;
            }
        },
        toggleReconstructedAncestorKey(ancestorKey) {
            this.selectedReconstructedAncestorKey = ancestorKey || ALL_RECONSTRUCTED_ANCESTORS;
        },
        colorForAncestor(ancestorKey) {
            return colorForAncestorKey(ancestorKey);
        },
        formatAncestorSummaryLabel(label) {
            const normalizedLabel = String(label || '').trim();
            if (normalizedLabel.length <= 38) {
                return normalizedLabel;
            }

            return normalizedLabel
                .split(/\s*&\s*/u)
                .map(part => this.abbreviatePersonName(part))
                .join(' & ');
        },
        abbreviatePersonName(name) {
            const particles = new Set([
                'al', 'da', 'de', 'del', 'den', 'der', 'di', 'du', 'het', 'la',
                'le', 'op', 'ten', 'ter', 'van', 'von', 'zu', 'zur',
            ]);
            const tokens = String(name || '').trim().split(/\s+/u).filter(Boolean);
            if (tokens.length <= 2) {
                return tokens.join(' ');
            }

            let surnameStart = tokens.length - 1;
            while (surnameStart > 0 && particles.has(tokens[surnameStart - 1].toLowerCase())) {
                surnameStart -= 1;
            }

            const givenTokens = tokens.slice(0, surnameStart);
            const surname = tokens.slice(surnameStart).join(' ');
            const initials = givenTokens
                .map(token => token.replace(/^[^A-Za-zÀ-ÖØ-öø-ÿ]+/u, '').charAt(0))
                .filter(Boolean)
                .map(letter => `${letter.toUpperCase()}.`)
                .join('');

            return initials ? `${initials} ${surname}` : tokens.join(' ');
        },
        getRelationshipHref(matchPersonID) {
            const selectedTester = this.dnaTesters[this.selectedCategory] || this.dnaTesters.Jeffrey;
            const rootPersonID = selectedTester?.rootPersonID || 'I43';
            const category = encodeURIComponent(this.selectedCategory || 'Rudy');
            const matchId = encodeURIComponent(matchPersonID || '');
            return this.localePath(`/relationship/${rootPersonID}/${matchPersonID}?source=dna&category=${category}&matchPersonID=${matchId}`);
        },
        isUnknownRelation(relation) {
            const normalizedRelation = String(relation || '').trim();
            return normalizedRelation === UNKNOWN_RELATION
                || normalizedRelation === INVESTIGATION_RELATION;
        },
        shouldLinkToPersonPage(match) {
            if (Array.isArray(match?.dnaInfo)) {
                return match.dnaInfo.length > 0
                    && match.dnaInfo.every(group => this.isUnknownRelation(group?.relation));
            }

            return this.isUnknownRelation(match?.dnaInfo?.relation);
        },
        getPersonHref(matchPersonID) {
            return this.localePath(`/person/${matchPersonID}`);
        },
        getMatchHref(match) {
            if (match.placeholder || !match.personID) return 'javascript:void(0)';
            if (this.shouldLinkToPersonPage(match)) {
                return this.getPersonHref(match.personID);
            }

            return this.getRelationshipHref(match.personID);
        },
        getMatchOverlayLabel(match) {
            return this.shouldLinkToPersonPage(match)
                ? this.$t('geneticMatches.cardOverlayPerson')
                : this.$t('geneticMatches.cardOverlayRelationship');
        },
        getMatchPrivateOverlayLabel(match) {
            return this.shouldLinkToPersonPage(match)
                ? this.$t('geneticMatches.cardOverlayPersonLogin')
                : this.$t('geneticMatches.cardOverlayLogin');
        },
        // getDefaultPhoto,
        // formatYearsWrapper(birthdate, altbirthdate, deathdate, burialdate, living) {
        //     return formatYears(birthdate, altbirthdate, deathdate, burialdate, living);
        // },
        // profileImageSrc() {
        //     if (!this.person) return ''; // If no person data, return an empty string
        //     if (this.person.profileImage) {
        //         return this.person.profileImage; // Return the profile image if available
        //     } else {
        //         // Return the default image based on gender
        //         return this.person.sex === 'M'
        //             ? require('@/assets/defaults/default_photo_male.svg')
        //             : require('@/assets/defaults/default_photo_female.svg');
        //     }
        // },
        setCategory(category) {
            if (this.isCategoryDisabled(category)) {
                return;
            }
            this.selectedCategory = category;
            this.router.push({ query: { ...this.route.query, filter: category } });
        },
        getCategoryLabel(category) {
            return category === ALL_CATEGORY ? this.$t('geneticMatches.allCategory') : category;
        },
        getCategoryCount(category) {
            if (this.selectedViewMode === 'chromosomeBrowser') {
                if (this.browserSubmode === 'ancestorPainting') {
                    const assignments = this.ancestorPainting.assignments || [];
                    if (category === ALL_CATEGORY) {
                        return assignments.length;
                    }
                    return assignments.filter(assignment => assignment.testerCategory === category).length;
                }

                if (category === ALL_CATEGORY) {
                    return this.allAvailableBrowserMatches.length;
                }
                return this.allAvailableBrowserMatches.filter(match => match.testerCategory === category).length;
            }

            if (category === ALL_CATEGORY) {
                return new Set(this.dnaMatches.map(m => m.matchKey || m.personID)).size;
            }
            return this.dnaMatches.filter(match => match.category === category).length;
        },
        isCategoryDisabled(category) {
            return this.getCategoryCount(category) === 0;
        },
        getBrowserMatchDisplayName(match) {
            if (isPrivateLivingPerson(match, this.authToken)) {
                return this.$t('home.nameHiddenPrivacy');
            }

            return match.name;
        },
        getBrowserMatchMeta(match) {
            if (this.selectedCategory === ALL_CATEGORY) {
                return `${match.testerCategory} · ${match.dnaInfo.sharedDNA}`;
            }

            return match.dnaInfo.sharedDNA;
        },
        getEvidenceMatchDisplayName(segment) {
            const match = this.dnaMatches.find(item => item.personID === segment?.matchPersonID);
            if (match && isPrivateLivingPerson(match, this.authToken)) {
                return this.$t('home.nameHiddenPrivacy');
            }

            return segment?.matchName || segment?.matchPersonID || '';
        },
        sanitizeReconstructedEvidence(ancestor, evidence = ancestor?.evidence || []) {
            return {
                ...ancestor,
                evidence: evidence.map(segment => ({
                    ...segment,
                    matchName: this.getEvidenceMatchDisplayName(segment),
                })),
            };
        },
        normalizeGender(gender) {
            const g = (gender || '').toLowerCase();
            return g === 'm' || g === 'male' ? 'male'
                : g === 'f' || g === 'female' ? 'female'
                    : 'unknown';
        },
        parseDNA(dnaString) {
            return parseFloat(String(dnaString).replace('%', '').replace(',', '.'));
        },
        parseSharedPercentage(sharedDNA) {
            const match = String(sharedDNA || '').match(/([\d.,]+)\s*%/);
            if (!match) {
                return 0;
            }

            return parseFloat(match[1].replace(',', '.')) || 0;
        },
        parseSharedCentimorgans(sharedDNA) {
            const match = String(sharedDNA || '').match(/\(([\d.,]+)\s*cM\)/i);
            if (!match) {
                return 0;
            }

            const rawValue = match[1];
            const normalizedValue = rawValue.includes(',')
                ? rawValue.replace(/\./g, '').replace(',', '.')
                : rawValue;
            return parseFloat(normalizedValue) || 0;
        },
        getSharedDnaSortMetrics(match) {
            const dnaInfo = Array.isArray(match?.dnaInfo) ? match.dnaInfo : [match?.dnaInfo];

            return dnaInfo.reduce((metrics, entry) => {
                const sharedDNA = entry?.sharedDNA || '';
                const isLessThanOnePercent = /^\s*<\s*1(?:[.,]0*)?\s*%+/i.test(sharedDNA);

                if (isLessThanOnePercent) {
                    metrics.lessThanOneCentimorgans += this.parseSharedCentimorgans(sharedDNA);
                } else {
                    metrics.percentage += this.parseSharedPercentage(sharedDNA);
                }

                return metrics;
            }, { percentage: 0, lessThanOneCentimorgans: 0 });
        },
        buildBrowserSummary(match, segments) {
            return {
                ...this.summarizeSegments(segments),
                sharedPercentage: this.parseSharedPercentage(match?.dnaInfo?.sharedDNA),
            };
        },
        hasUsableLargestSegment(match) {
            return Number.isFinite(this.parseDNA(match?.dnaInfo?.largestSegment));
        },
        setSort(field) {
            if (this.selectedSort === field) {
                this.sortAscending = !this.sortAscending;
            } else {
                this.selectedSort = field;
                this.sortAscending = field === 'name';
            }

            if (this.selectedViewMode === 'cards') {
                this.router.push({
                    query: {
                        ...this.route.query,
                        view: 'cards',
                        sortField: this.selectedSort,
                        sortOrder: this.sortAscending ? 'asc' : 'desc',
                    },
                });
            }
        },
        setViewMode(mode) {
            const nextMode = normalizeViewMode(mode);
            this.selectedViewMode = nextMode;
            const nextQuery = { ...this.route.query, view: nextMode };
            if (nextMode === 'chromosomeBrowser') {
                this.browserSubmode = 'ancestorPainting';
                nextQuery.browserMode = 'ancestorPainting';
                this.refreshAncestorPainting();
            } else if (nextMode === 'reconstructedAncestors') {
                this.selectedCategory = ALL_CATEGORY;
                delete nextQuery.browserMode;
                delete nextQuery.filter;
                this.refreshReconstructedAncestors();
            } else {
                delete nextQuery.browserMode;
            }
            if (nextMode === 'cards') {
                nextQuery.sortField = this.selectedSort;
                nextQuery.sortOrder = this.sortAscending ? 'asc' : 'desc';
            } else {
                delete nextQuery.sortField;
                delete nextQuery.sortOrder;
            }
            // Persist mode in query parameters so deep links reopen the same UI mode.
            this.router.push({ query: nextQuery });
        },
        setBrowserSubmode(mode) {
            const nextMode = normalizeBrowserSubmode(mode);
            this.browserSubmode = nextMode;
            const nextQuery = { ...this.route.query, browserMode: nextMode };
            this.router.push({ query: nextQuery });
            if (nextMode === 'ancestorPainting') {
                this.refreshAncestorPainting();
            }
        },
        normalizeReconstructedRoute() {
            const legacyReconstructedMode = this.route.query.view === 'chromosomeBrowser'
                && this.route.query.browserMode === 'reconstructedAncestors';
            const reconstructedModeWithStaleQuery = this.route.query.view === 'reconstructedAncestors'
                && (this.route.query.browserMode || this.route.query.filter);

            if (!legacyReconstructedMode && !reconstructedModeWithStaleQuery) {
                return false;
            }

            const nextQuery = {
                ...this.route.query,
                view: 'reconstructedAncestors',
            };
            delete nextQuery.browserMode;
            delete nextQuery.filter;
            this.router.replace({ query: nextQuery });
            return true;
        },
        selectBrowserMatch(entryId) {
            this.selectedBrowserEntryId = entryId;
        },
        setShowAncestryOverlay(nextValue) {
            this.showAncestryOverlay = Boolean(nextValue);
        },
        setSelectedHaplogroupIds(nextIds) {
            this.selectedHaplogroupIds = Array.isArray(nextIds) ? nextIds : [];
        },
        focusHaplogroupRoute(haplogroupId) {
            if (!haplogroupId) return;
            if (!this.selectedHaplogroupIds.includes(haplogroupId)) {
                this.selectedHaplogroupIds = [...this.selectedHaplogroupIds, haplogroupId];
            }
            this.focusedHaplogroupId = '';
            this.$nextTick(() => {
                this.focusedHaplogroupId = haplogroupId;
            });
        },
        formatOneDecimal(value) {
            return Number(value || 0).toFixed(1);
        },
        formatInteger(value) {
            return new Intl.NumberFormat(this.locale || undefined).format(Math.round(value || 0));
        },
        sortMatches(list) {
            // One centralized sorter keeps card and browser lists consistent.
            return [...list].sort((a, b) => {
                const dir = this.sortAscending ? 1 : -1;
                if (this.selectedSort === 'name') {
                    return dir * a.name.localeCompare(b.name);
                }
                const getSegments = (match) => {
                    if (Array.isArray(match.dnaInfo)) {
                        return Math.max(...match.dnaInfo.map(d => Number(d.sharedSegments) || 0));
                    }
                    return Number(match.dnaInfo.sharedSegments) || 0;
                };
                if (this.selectedSort === 'segments') {
                    return dir * (getSegments(a) - getSegments(b));
                }
                const getLargest = (match) => {
                    if (Array.isArray(match.dnaInfo)) {
                        return Math.max(...match.dnaInfo.map(d => {
                            const parsed = this.parseDNA(d.largestSegment);
                            return Number.isFinite(parsed) ? parsed : 0;
                        }));
                    }
                    const parsed = this.parseDNA(match.dnaInfo.largestSegment);
                    return Number.isFinite(parsed) ? parsed : 0;
                };
                if (this.selectedSort === 'largestSegment') {
                    return dir * (getLargest(a) - getLargest(b));
                }
                const dnaA = this.getSharedDnaSortMetrics(a);
                const dnaB = this.getSharedDnaSortMetrics(b);
                const percentageDifference = dnaA.percentage - dnaB.percentage;
                if (percentageDifference !== 0) {
                    return dir * percentageDifference;
                }

                // Providers sometimes only report "<1%". Within that otherwise
                // equal group, use the exact cM amount instead of source order.
                const centimorganDifference = dnaA.lessThanOneCentimorgans - dnaB.lessThanOneCentimorgans;
                if (centimorganDifference !== 0) {
                    return dir * centimorganDifference;
                }

                const nameDifference = String(a.name || '').localeCompare(String(b.name || ''), 'nl', { sensitivity: 'base' });
                if (nameDifference !== 0) {
                    return dir * nameDifference;
                }
                return dir * String(a.matchKey || a.personID || '').localeCompare(String(b.matchKey || b.personID || ''));
            });
        },
        buildDisplayMatch(match) {
            const normalizedGender = this.normalizeGender(match.gender);
            return {
                ...match,
                testerCategory: match.category,
                entryId: match.entryId || `${match.category}:${match.matchKey || match.personID}`,
                gender: normalizedGender,
                name: match.placeholder ? '' : (match.name || ''),
                years: formatYears(match.birthdate, match.altbirthdate, match.deathdate, match.burialdate, match.living, this.locale),
                photoSrc: match.placeholder ? '' : (match.photoSrc || getDefaultPhoto(normalizedGender)),
            };
        },
        ensureBrowserSelection() {
            // Always keep a valid selected entry to prevent empty-state flicker when
            // filters, sorting, or data availability changes.
            if (this.availableBrowserMatches.length === 0) {
                this.selectedBrowserEntryId = '';
                this.browserSegments = [];
                this.browserSummary = createEmptyBrowserSummary();
                this.browserError = '';
                this.browserLoading = false;
                return;
            }

            const hasCurrent = this.availableBrowserMatches.some(m => m.entryId === this.selectedBrowserEntryId);
            if (!hasCurrent) {
                this.selectedBrowserEntryId = this.availableBrowserMatches[0].entryId;
            }
        },
        async loadSelectedBrowserSegments() {
            if (!this.selectedBrowserEntryId) {
                return;
            }

            const selectedMatch = this.availableBrowserMatches.find(match => match.entryId === this.selectedBrowserEntryId);
            const entry = selectedMatch
                ? this.getBrowserEntry(selectedMatch.testerCategory, selectedMatch.personID, selectedMatch.matchKey)
                : null;

            if (!entry) {
                this.browserSegments = [];
                this.browserSummary = createEmptyBrowserSummary();
                this.browserError = this.$t('geneticMatches.chromosomeBrowser.noAvailableCsv');
                this.browserAncestryOverlay = {
                    available: false,
                    testerCategory: '',
                    slicesBySegmentKey: {},
                    legend: [],
                };
                return;
            }

            this.browserLoading = true;
            this.browserAncestryLoading = false;
            this.browserError = '';

            try {
                // Segment data is required; ancestry overlay is optional enrichment.
                const segments = await this.loadSegmentsForEntry(entry);
                this.browserSegments = segments;
                this.browserSummary = this.buildBrowserSummary(selectedMatch, segments);

                if (this.showAncestryOverlay) {
                    this.browserAncestryLoading = true;
                    try {
                        const overlay = await this.loadAncestryOverlayForEntry(entry);
                        this.browserAncestryOverlay = overlay || {
                            available: false,
                            testerCategory: entry.testerCategory,
                            slicesBySegmentKey: {},
                            legend: [],
                        };
                    } catch (overlayError) {
                        this.browserAncestryOverlay = {
                            available: false,
                            testerCategory: entry.testerCategory,
                            slicesBySegmentKey: {},
                            legend: [],
                        };
                        console.error(overlayError);
                    } finally {
                        this.browserAncestryLoading = false;
                    }
                } else {
                    this.browserAncestryOverlay = {
                        available: false,
                        testerCategory: entry.testerCategory,
                        slicesBySegmentKey: {},
                        legend: [],
                    };
                }
            } catch (error) {
                this.browserSegments = [];
                this.browserSummary = createEmptyBrowserSummary();
                this.browserError = this.$t('geneticMatches.chromosomeBrowser.loadError');
                this.browserAncestryOverlay = {
                    available: false,
                    testerCategory: '',
                    slicesBySegmentKey: {},
                    legend: [],
                };
                console.error(error);
            } finally {
                this.browserLoading = false;
                this.browserAncestryLoading = false;
            }
        },
    },
    watch: {
        // Keep local view state in sync with URL-based deep links while ignoring
        // removed or unknown modes.
        'route.query.view'(newView) {
            if (this.normalizeReconstructedRoute()) {
                return;
            }
            this.selectedViewMode = normalizeViewMode(newView);
            if (this.selectedViewMode === 'cards') {
                this.selectedSort = normalizeSortField(this.route.query.sortField);
                this.sortAscending = normalizeSortAscending(this.route.query.sortOrder, this.selectedSort);
            }
            if (this.selectedViewMode === 'chromosomeBrowser' && this.browserSubmode === 'ancestorPainting') {
                this.refreshAncestorPainting();
            } else if (this.selectedViewMode === 'reconstructedAncestors') {
                this.selectedCategory = ALL_CATEGORY;
                this.refreshReconstructedAncestors();
            }
        },
        'route.query.browserMode'(newMode) {
            if (this.normalizeReconstructedRoute()) {
                return;
            }
            this.browserSubmode = normalizeBrowserSubmode(newMode);
            if (this.selectedViewMode === 'chromosomeBrowser' && this.browserSubmode === 'ancestorPainting') {
                this.refreshAncestorPainting();
            }
        },
        'route.query.sortField'(newSortField) {
            if (this.selectedViewMode !== 'cards') {
                return;
            }
            this.selectedSort = normalizeSortField(newSortField);
            this.sortAscending = normalizeSortAscending(this.route.query.sortOrder, this.selectedSort);
        },
        'route.query.sortOrder'(newSortOrder) {
            if (this.selectedViewMode !== 'cards') {
                return;
            }
            this.sortAscending = normalizeSortAscending(newSortOrder, this.selectedSort);
        },
        // Keep local category in sync with URL-based filtering.
        'route.query.filter'(newFilter) {
            if (this.selectedViewMode === 'reconstructedAncestors') {
                this.selectedCategory = ALL_CATEGORY;
                this.ensureBrowserSelection();
                return;
            }
            if (newFilter && this.categoryOptions.includes(newFilter)) {
                this.selectedCategory = newFilter;
            } else {
                this.selectedCategory = ALL_CATEGORY;
            }
            this.ensureBrowserSelection();
        },
        selectedSort() {
            this.ensureBrowserSelection();
        },
        sortAscending() {
            this.ensureBrowserSelection();
        },
        selectedCategory() {
            this.ensureBrowserSelection();
            this.ensurePaintingAncestorSelection();
            this.ensureReconstructedAncestorSelection();
        },
        selectedBrowserEntryId() {
            this.loadSelectedBrowserSegments();
        },
        browserEntriesIndex() {
            this.ensureBrowserSelection();
        },
        showAncestryOverlay() {
            this.loadSelectedBrowserSegments();
        },
    },
    computed: {
        viewModes() {
            return VIEW_MODES;
        },
        categoryOptions() {
            return [ALL_CATEGORY, ...this.dnaTesterOrder];
        },
        showCategoryButtons() {
            return this.selectedViewMode !== 'network'
                && this.selectedViewMode !== 'haplogroups'
                && this.selectedViewMode !== 'reconstructedAncestors';
        },
        showCategoryCounts() {
            return true;
        },
        showSortControls() {
            if (this.selectedViewMode === 'network'
                || this.selectedViewMode === 'haplogroups'
                || this.selectedViewMode === 'reconstructedAncestors') {
                return false;
            }

            if (this.selectedViewMode === 'chromosomeBrowser') {
                if (this.browserSubmode === 'ancestorPainting') {
                    return false;
                }
                if (this.browserSubmode === 'match' && this.selectedCategory === ALL_CATEGORY) {
                    return false;
                }
            }

            return true;
        },
        sortIconSrc() {
            return this.sortAscending ? sortAscSvg : sortDscSvg;
        },
        filteredMatches() {
            // In "all" mode, merge same person across testers while preserving each
            // tester-specific dnaInfo entry for detailed card rendering.
            let result;

            if (this.selectedCategory === ALL_CATEGORY) {
                const grouped = {};
                this.dnaMatches.forEach(match => {
                    const normalizedGender = this.normalizeGender(match.gender);
                    const personKey = match.matchKey || match.personID;
                    if (!grouped[personKey]) {
                        grouped[personKey] = {
                            ...match,
                            gender: normalizedGender,
                            years: formatYears(match.birthdate, match.altbirthdate, match.deathdate, match.burialdate, match.living, this.locale),
                            name: match.placeholder ? '' : (match.name || ''),
                            photoSrc: match.placeholder ? '' : (match.photoSrc || getDefaultPhoto(normalizedGender)),
                            dnaInfo: [{ tester: match.category, ...match.dnaInfo }],
                        };
                    } else {
                        grouped[personKey].dnaInfo.push({ tester: match.category, ...match.dnaInfo });
                    }
                });
                result = Object.values(grouped);
            } else {
                result = this.dnaMatches
                    .filter(match => match.category === this.selectedCategory)
                    .map(match => {
                        const normalizedGender = this.normalizeGender(match.gender);
                        return {
                            ...match,
                            gender: normalizedGender,
                            years: formatYears(match.birthdate, match.altbirthdate, match.deathdate, match.burialdate, match.living, this.locale),
                            name: match.placeholder ? '' : (match.name || ''),
                            photoSrc: match.placeholder ? '' : (match.photoSrc || getDefaultPhoto(normalizedGender)),
                        };
                    });
            }

            return this.sortMatches(result);
        },
        allBrowserMatches() {
            // Browser mode only works with entries that have corresponding CSV segments.
            const prepared = this.dnaMatches.map(match => this.buildDisplayMatch(match));
            const withCsv = prepared.filter(match => this.hasSegmentCsv(match.testerCategory, match.personID, match.matchKey));
            return this.sortMatches(withCsv);
        },
        allAvailableBrowserMatches() {
            // Filter alleen matches met een geldige grootste segmentwaarde (geen '-')
            const isValidLargest = match => {
                const val = match?.dnaInfo?.largestSegment;
                const parsed = this.parseDNA(val);
                return Number.isFinite(parsed) && parsed > 0;
            };
            return this.allBrowserMatches.filter(isValidLargest);
        },
        availableBrowserMatches() {
            let list = this.allAvailableBrowserMatches;
            if (!this.isKnownCategory(this.selectedCategory) || this.selectedCategory === ALL_CATEGORY) {
                return list;
            }
            return list.filter(match => match.testerCategory === this.selectedCategory);
        },
        filteredPaintingAssignments() {
            const assignments = this.ancestorPainting.assignments || [];
            if (!this.isKnownCategory(this.selectedCategory) || this.selectedCategory === ALL_CATEGORY) {
                return assignments;
            }
            return assignments.filter(assignment => assignment.testerCategory === this.selectedCategory);
        },
        filteredReconstructedAncestors() {
            const ancestors = this.reconstructedAncestors.ancestors || [];
            return ancestors.map(ancestor => this.sanitizeReconstructedEvidence(ancestor));
        },
        filteredReconstructedSummaryByAncestor() {
            return [...this.filteredReconstructedAncestors]
                .sort((a, b) => Number(b.assignedCentimorgans || 0) - Number(a.assignedCentimorgans || 0)
                    || String(a.ancestorLabel || '').localeCompare(String(b.ancestorLabel || '')));
        },
        displayedReconstructedAncestors() {
            if (this.selectedReconstructedAncestorKey === ALL_RECONSTRUCTED_ANCESTORS) {
                return this.filteredReconstructedAncestors;
            }
            return this.filteredReconstructedAncestors
                .filter(ancestor => ancestor.ancestorKey === this.selectedReconstructedAncestorKey);
        },
        displayedReconstructedTotals() {
            if (this.selectedReconstructedAncestorKey === ALL_RECONSTRUCTED_ANCESTORS) {
                return this.filteredReconstructedTotals;
            }

            const includedSegmentCount = this.displayedReconstructedAncestors
                .reduce((sum, ancestor) => sum + Number(ancestor.segmentCount || 0), 0);
            const assignedCentimorgans = Number(this.displayedReconstructedAncestors
                .reduce((sum, ancestor) => sum + Number(ancestor.assignedCentimorgans || 0), 0)
                .toFixed(2));
            const matchCount = this.displayedReconstructedAncestors
                .reduce((sum, ancestor) => sum + Number(ancestor.matchCount || 0), 0);

            return {
                ...this.filteredReconstructedTotals,
                reconstructedAncestorCount: this.displayedReconstructedAncestors.length,
                includedSegmentCount,
                assignedCentimorgans,
                matchCount,
            };
        },
        filteredReconstructedTotals() {
            return this.reconstructedAncestors.totals;
        },
        paintingAutosomalDenominatorCm() {
            const totalEstimate = Number(this.ancestorPainting?.totals?.autosomalCmTotalEstimate)
                || DEFAULT_AUTOSOMAL_CM_TOTAL_ESTIMATE;
            const testerCount = new Set(this.filteredPaintingAssignments
                .map(assignment => assignment.testerCategory)
                .filter(Boolean)).size || 1;
            return totalEstimate * testerCount;
        },
        filteredPaintingSummaryByAncestor() {
            const denominator = this.paintingAutosomalDenominatorCm;

            return summarizePaintingCoverage(this.filteredPaintingAssignments)
                .map(summary => ({
                    ...summary,
                    centimorgans: Number(summary.centimorgans.toFixed(2)),
                    totalDnaPercentage: denominator > 0 ? (summary.centimorgans / denominator) * 100 : 0,
                }))
                .sort((a, b) => b.centimorgans - a.centimorgans || a.ancestorLabel.localeCompare(b.ancestorLabel))
                .slice(0, 12);
        },
        selectedBrowserLabel() {
            const selected = this.availableBrowserMatches.find(m => m.entryId === this.selectedBrowserEntryId);
            if (!selected) {
                return '';
            }
            return `${this.getBrowserMatchDisplayName(selected)} · ${selected.testerCategory}`;
        },
        selectedBrowserTesterInfo() {
            const selected = this.availableBrowserMatches.find(m => m.entryId === this.selectedBrowserEntryId);
            if (!selected) return null;
            const testerCat = selected.testerCategory;
            const tester = this.dnaTesters[testerCat];
            return {
                name: tester?.fullName || testerCat,
                photoSrc: tester?.rootPersonID
                    ? `${process.env.VUE_APP_BACKEND_API_URL}/person-default-photo/${tester.rootPersonID}`
                    : getDefaultPhoto('male'),
            };
        },
        selectedBrowserMatchInfo() {
            const selected = this.availableBrowserMatches.find(m => m.entryId === this.selectedBrowserEntryId);
            if (!selected) return null;
            return {
                name: this.getBrowserMatchDisplayName(selected),
                photoSrc: selected.photoSrc,
            };
        },
    },
    async created() {
        this.normalizeReconstructedRoute();
        await this.refreshDnaPrivacyData();
        await this.refreshBrowserEntries();
        if (this.selectedViewMode === 'chromosomeBrowser' && this.browserSubmode === 'ancestorPainting') {
            await this.refreshAncestorPainting();
        } else if (this.selectedViewMode === 'reconstructedAncestors') {
            this.selectedCategory = ALL_CATEGORY;
            await this.refreshReconstructedAncestors();
        }
        this.ensureBrowserSelection();
    },
};
</script>

<style scoped>
.dna-matches {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.empty-state {
    color: #9C9C9C;
    text-align: center;
    padding: 48px 0;
}

.sort-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.sort-controls button {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 500;
    border-radius: var(--border-radius-pill);
    border: 1.5px solid #D6D6D6;
    color: #555;
    background-color: transparent;
    cursor: pointer;
    transition: var(--transition-ease);
}

.sort-controls button:hover {
    background-color: var(--brand-bg-2);
    border-color: #aaa;
}

.sort-controls button.active {
    border-color: var(--brand-text-dark);
    background-color: var(--brand-bg-2);
    color: var(--brand-text-dark);
    font-weight: 600;
}

.sort-direction-icon {
    width: 8px;
    height: 13px;
}

.sort-label {
    font-size: 13px;
    color: #9C9C9C;
    font-weight: 600;
    display: none;
}

.view-mode-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.view-mode-controls button {
    display: flex;
    padding: 8px 16px;
    align-items: center;
    gap: 8px;
    border: none;
    cursor: pointer;
    border-radius: var(--border-radius-pill);
    border: 2px solid var(--brand-color-1);
    color: var(--brand-color-1);
    font-weight: 600;
    background-color: rgba(0, 0, 0, 0);
    transition: var(--transition-ease);
}

.view-mode-controls button:hover {
    background-color: rgba(174, 30, 37, 0.1);
}

.view-mode-controls button.active {
    background-color: var(--brand-color-1);
    color: var(--brand-text-light);
}

.view-mode-label {
    font-size: 13px;
    color: #9C9C9C;
    font-weight: 600;
    display: none;
}

.browser-mode-layout {
    display: grid;
    grid-template-columns: minmax(260px, 360px) minmax(0, 1fr);
    gap: 24px;
}

.browser-sidebar {
    border-radius: var(--border-radius-xl);
    background: var(--brand-bg-1);
    box-shadow: var(--shadow-card-sm);
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 900px;
    overflow-y: auto;
}

.browser-sidebar h3 {
    margin: 0;
}

.browser-submode-controls {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    margin-bottom: 8px;
}

.browser-submode-controls button {
    border: 1px solid #e1e1e1;
    border-radius: var(--border-radius-lg);
    padding: 9px 11px;
    background: #fff;
    color: #555;
    text-align: left;
    font-weight: 600;
    cursor: pointer;
    transition: var(--transition-ease);
}

.browser-submode-controls button:hover {
    background: var(--brand-bg-2);
}

.browser-submode-controls button.active {
    border-color: var(--brand-color-1);
    color: var(--brand-color-1);
    box-shadow: inset 0 0 0 1px var(--brand-color-1);
}

.browser-match-button {
    border: 1px solid #e4e4e4;
    border-radius: var(--border-radius-lg);
    padding: 10px 12px;
    background: #fff;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 4px;
    cursor: pointer;
    transition: var(--transition-ease);
}

.browser-match-button strong {
    color: var(--brand-text-dark);
    font-size: 14px;
}

.browser-match-button span {
    color: #777;
    font-size: 12px;
}

.browser-match-button:hover {
    border-color: #cfcfcf;
    background: var(--brand-bg-2);
}

.browser-match-button.active {
    border-color: var(--brand-color-1);
    box-shadow: inset 0 0 0 1px var(--brand-color-1);
}

.browser-empty-state {
    padding-top: 16px;
    text-align: left;
}

.painting-summary-list {
    padding-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.painting-summary-list h4 {
    margin: 0;
    font-size: 13px;
}

.painting-summary-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
    border: 1px solid #ececec;
    border-radius: var(--border-radius-lg);
    padding: 8px 10px;
    background: #fff;
    color: var(--brand-text-dark);
    cursor: pointer;
    font: inherit;
    text-align: left;
    transition: var(--transition-ease);
}

.painting-summary-row:hover,
.painting-summary-row:focus-visible,
.painting-summary-row.active {
    border-color: var(--brand-color-1);
    box-shadow: inset 0 0 0 1px var(--brand-color-1);
}

.painting-summary-main {
    display: grid;
    grid-template-columns: 12px minmax(0, 1fr);
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.painting-summary-color {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    display: inline-block;
}

.painting-summary-main strong {
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.reconstructed-summary-row {
    gap: 4px;
}

.reconstructed-summary-row .painting-summary-main {
    grid-template-columns: minmax(0, 1fr);
}

.reconstructed-summary-meta {
    color: #777;
    font-size: 11px;
}

.browser-view {
    min-width: 0;
}

.network-mode-view {
    border-radius: var(--border-radius-xl);
    overflow: hidden;
    width: 100%;
    height: 100%;
    max-width: 100%;
}

.haplogroup-mode-view {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.haplogroup-globe-panel {
    position: relative;
    height: var(--viewport-panel-height-desktop);
    min-height: var(--viewport-panel-height-desktop);
    border-radius: var(--border-radius-xl);
    overflow: hidden;
    box-shadow: var(--shadow-card-sm);
}

.haplogroup-proof-overlay {
    position: absolute;
    bottom: 18px;
    left: 18px;
    max-width: min(360px, calc(100% - 36px));
    max-height: calc(100% - 36px);
    z-index: 25;
    pointer-events: auto;
}

.dna-network-container {
    background-color: white;
    width: 100%;
    margin: 0 auto;
    height: var(--viewport-panel-height-desktop);
    min-height: var(--viewport-panel-height-desktop);
    padding: 1rem;
    border-radius: var(--border-radius-xl);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

@media (max-width: 860px) {
    .sort-controls {
        flex-wrap: nowrap;
        width: 100vw;
        margin-left: -20px;
        padding: 0 20px;
        overflow-x: scroll;
        scrollbar-width: none;
        -ms-overflow-style: none;
    }

    .sort-controls::-webkit-scrollbar {
        display: none;
    }

    .sort-controls button {
        flex-shrink: 0;
    }

    .view-mode-controls {
        flex-wrap: nowrap;
        width: 100vw;
        margin-left: -20px;
        padding: 0 20px;
        overflow-x: scroll;
        scrollbar-width: none;
        -ms-overflow-style: none;
    }

    .view-mode-controls::-webkit-scrollbar {
        display: none;
    }

    .view-mode-controls button {
        flex-shrink: 0;
    }

    .browser-mode-layout {
        grid-template-columns: 1fr;
    }

    .browser-sidebar {
        max-height: 260px;
    }

    .dna-network-container,
    .haplogroup-globe-panel {
        height: auto;
        min-height: min(65dvh, 560px);
    }

    .haplogroup-proof-overlay {
        bottom: 12px;
        left: 12px;
        max-width: calc(100% - 24px);
    }
}
</style>
